<template>
  <div class="general-settings">
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">
          <Building :size="20" class="me-2" />
          Company Information
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading settings...
        </div>
        <form v-else @submit.prevent="saveSettings">
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Company Name</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.companyName"
                placeholder="Enter company name"
              />
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Company Logo URL</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.companyLogo"
                placeholder="https://example.com/logo.png"
              />
              <small class="text-muted">Used in emails and invoices</small>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <label class="form-label">Company Address</label>
              <textarea 
                class="form-control" 
                rows="3"
                v-model="settings.companyAddress"
                placeholder="Enter full company address"
              ></textarea>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="form-label">Phone</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.companyPhone"
                placeholder="(555) 123-4567"
              />
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label">Email</label>
              <input 
                type="email" 
                class="form-control" 
                v-model="settings.companyEmail"
                placeholder="info@company.com"
              />
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label">Website</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.companyWebsite"
                placeholder="https://www.company.com"
              />
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <Save :size="16" class="me-1" />
              {{ saving ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Building, Save } from 'lucide-vue-next';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';

const loading = ref(true);
const saving = ref(false);

const settings = ref({
  companyName: '',
  companyLogo: '',
  companyAddress: '',
  companyPhone: '',
  companyEmail: '',
  companyWebsite: ''
});

const loadSettings = async () => {
  try {
    loading.value = true;
    const options = await ApiService.getOptions('Company');
    settings.value = {
      companyName: options.find((o: any) => o.Key === 'CompanyName')?.Value || '',
      companyLogo: options.find((o: any) => o.Key === 'CompanyLogo')?.Value || '',
      companyAddress: options.find((o: any) => o.Key === 'CompanyAddress')?.Value || '',
      companyPhone: options.find((o: any) => o.Key === 'CompanyPhone')?.Value || '',
      companyEmail: options.find((o: any) => o.Key === 'CompanyEmail')?.Value || '',
      companyWebsite: options.find((o: any) => o.Key === 'CompanyWebsite')?.Value || ''
    };
  } catch (error) {
    console.error('Error loading settings:', error);
    Alert.notify({ text: 'Failed to load settings', type: 'error' });
  } finally {
    loading.value = false;
  }
};

const saveSettings = async () => {
  try {
    saving.value = true;
    await ApiService.saveOptions('Company', [
      { Key: 'CompanyName', Value: settings.value.companyName },
      { Key: 'CompanyLogo', Value: settings.value.companyLogo },
      { Key: 'CompanyAddress', Value: settings.value.companyAddress },
      { Key: 'CompanyPhone', Value: settings.value.companyPhone },
      { Key: 'CompanyEmail', Value: settings.value.companyEmail },
      { Key: 'CompanyWebsite', Value: settings.value.companyWebsite }
    ]);
    Alert.notify({ text: 'Settings saved successfully', type: 'success' });
  } catch (error) {
    console.error('Error saving settings:', error);
    Alert.notify({ text: 'Failed to save settings', type: 'error' });
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadSettings();
});
</script>

<style scoped>
.general-settings {
  max-width: 900px;
}
</style>
